/**
 * GuidancePromptCard — post-eligibility next-best-action card for the chat panel.
 *
 * Rendered in WorkspaceChatPanel after the document upload queue is exhausted.
 * Shows a contextual assistant message guiding the user to the single most
 * important next step in their N-400 journey.
 *
 * Pure display component: the parent (CaseWorkspacePage) computes the NBA and
 * passes it as a typed prop. No API calls here.
 */
import { Link } from "react-router-dom";

// ─── NBA type ─────────────────────────────────────────────────────────────────

export type NBA =
  | { type: "missing_fields"; items: { message: string; wizardPath: string }[] }
  | { type: "risk_flags" }
  | { type: "ready_unpaid"; feeDollars: string; checkoutPath: string }
  | { type: "paid_no_pdf"; isReady: boolean; reviewPath: string }
  | { type: "pdf_ready"; casePath: string };

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  nba: NBA;
}

export function GuidancePromptCard({ nba }: Props) {
  return (
    <div className="flex gap-2.5">
      <AssistantIcon />
      <div className="bg-slate-50 border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3.5 max-w-[85%] min-w-0">
        {nba.type === "missing_fields" && <MissingFieldsContent nba={nba} />}
        {nba.type === "risk_flags"     && <RiskFlagsContent />}
        {nba.type === "ready_unpaid"   && <ReadyUnpaidContent nba={nba} />}
        {nba.type === "paid_no_pdf"    && <PaidNoPdfContent nba={nba} />}
        {nba.type === "pdf_ready"      && <PdfReadyContent nba={nba} />}
      </div>
    </div>
  );
}

// ─── Branch renderers ─────────────────────────────────────────────────────────

function MissingFieldsContent({
  nba,
}: {
  nba: Extract<NBA, { type: "missing_fields" }>;
}) {
  return (
    <>
      <p className="text-sm text-slate-700 leading-relaxed mb-3">
        Before generating your N-400 draft, a few required sections still need
        your input. Here are the top items to complete:
      </p>
      <ul className="space-y-2 mb-4">
        {nba.items.map((item) => (
          <li key={item.wizardPath} className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-1.5 min-w-0">
              <div className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0 mt-1.5" />
              <span className="text-xs text-slate-600 leading-relaxed">{item.message}</span>
            </div>
            <Link
              to={item.wizardPath}
              className="shrink-0 text-[10px] font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-2 py-0.5 rounded-md transition-colors whitespace-nowrap"
            >
              Fix →
            </Link>
          </li>
        ))}
      </ul>
      <p className="text-xs text-slate-400">
        The{" "}
        <span className="font-medium text-slate-500">Status</span>
        {" "}panel shows all missing items. You can also{" "}
        <Link
          to={nba.items[0]?.wizardPath.replace(/\/[^/]+$/, "") ?? "#"}
          className="text-blue-600 hover:underline"
        >
          open the form editor
        </Link>
        {" "}to work through them in order.
      </p>
    </>
  );
}

function RiskFlagsContent() {
  return (
    <p className="text-sm text-slate-700 leading-relaxed">
      Your application has a few items flagged for review — these don't
      automatically disqualify you, but you should read through them carefully
      before filing. They're listed in the{" "}
      <span className="font-medium text-slate-600">Status</span> panel. If
      you're unsure about any of them, consulting a licensed immigration
      attorney is the safest next step.
    </p>
  );
}

function ReadyUnpaidContent({
  nba,
}: {
  nba: Extract<NBA, { type: "ready_unpaid" }>;
}) {
  return (
    <>
      <p className="text-sm text-slate-700 leading-relaxed mb-3">
        ✓ Your N-400 looks complete — all required fields are filled in. When
        you're ready, pay once to unlock and download your formatted draft.
        USCIS filing fees are separate and paid directly to USCIS.
      </p>
      <Link
        to={nba.checkoutPath}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 px-4 py-2 rounded-xl transition-colors"
      >
        Pay ${nba.feeDollars} to download →
      </Link>
    </>
  );
}

function PaidNoPdfContent({
  nba,
}: {
  nba: Extract<NBA, { type: "paid_no_pdf" }>;
}) {
  return (
    <>
      <p className="text-sm text-slate-700 leading-relaxed mb-3">
        {nba.isReady
          ? "Your fee is paid and your application is complete. Generate your N-400 packet now — it usually takes under a minute."
          : "Your fee is paid. Complete the remaining required fields, then generate your N-400 packet."}
      </p>
      <Link
        to={nba.reviewPath}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 px-4 py-2 rounded-xl transition-colors"
      >
        {nba.isReady ? "Generate packet →" : "Continue application →"}
      </Link>
    </>
  );
}

function PdfReadyContent({
  nba,
}: {
  nba: Extract<NBA, { type: "pdf_ready" }>;
}) {
  return (
    <>
      <p className="text-sm text-slate-700 leading-relaxed mb-3">
        Your N-400 packet is ready to download. Review every answer carefully
        before filing — USCIS filing fees are separate and paid directly to
        USCIS, not through this tool.
      </p>
      <Link
        to={nba.casePath}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 px-4 py-2 rounded-xl transition-colors"
      >
        Download PDF →
      </Link>
    </>
  );
}

// ─── Icon helper ─────────────────────────────────────────────────────────────

function AssistantIcon() {
  return (
    <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center shrink-0 mt-0.5">
      <svg
        className="w-3.5 h-3.5 text-white"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z"
        />
      </svg>
    </div>
  );
}
